// Full-page overlay Settings (Shopify pattern — Volkan direction 2026-05-23).
//
// Settings is rendered as a fixed-position overlay on top of the active shell
// (admin/seller/supply). X button + ESC closes via router history-back.
//
// Section taxonomy (Volkan 10-madde expansion 2026-05-23) — three groups:
//
//   Organization (admin + sellers + vendors)
//     - Organization        (Shopify-pattern business details mock UI)
//     - Users               (Team Management; renamed at section label level)
//     - Plan                (sellers only — subscription tier placeholder M3)
//
//   Store (sellers) / Platform Master (admin)
//     - Shipping & delivery → Tag Management (+ Printing Setup, admin only: one platform-wide row)
//     - Store Integrations  (sellers only — Etsy/Shopify/WC/Amazon)
//     - Wallet preferences  (sellers only — complements sidebar Wallet)
//
//   System (admin + sellers + vendors)
//     - Notifications       (placeholder M3)
//     - Security            (placeholder M3)
//     - Languages           (placeholder M3)
//
// Admin sees the middle group labelled "Platform Master" (admin-owned scope
// per D-6) and only Shipping & delivery within it; Store Integrations + Wallet
// preferences are sellers-only.
//
// Header: surface-aware org name + scope sub-label (left) + client-side search
// (centre) + close button (right). Search filters section labels live.
//
// Shipping & delivery is a parent grouping for two existing sub-pages:
//   - Tags    → TagManagement (mock CRUD already live)
//   - Printing → PrintingSetup (mock printers + bench setup already live)
//
// M3 Stage 4 SPEC.md Faz 1 / Faz 4 / Faz 5 / Faz 6 ratify backend wiring for
// the placeholder sections.

import { useEffect, useMemo, useState } from 'react'
import { useRouter } from '@tanstack/react-router'
import {
  Bell,
  Palette,
  Building2,
  Camera,
  ChevronRight,
  CreditCard,
  Languages as LanguagesIcon,
  Link2,
  Plug,
  Printer,
  Search,
  ShieldCheck,
  Store,
  Truck,
  Users as UsersIcon,
  Wallet,
  X,
  type LucideIcon,
} from 'lucide-react'
import { TagManagement } from './TagManagement'
import { TeamManagement } from './TeamManagement'
import { useMe } from '../../lib/useMe'
import { SellerTeam } from './SellerTeam'
import { PrintingSetup } from './PrintingSetup'
import { Organization } from './Organization'
import { Plan } from './Plan'
import { StoreIntegrations } from './StoreIntegrations'
import { ListingMapping } from './ListingMapping'
import { ShipStationCredential } from './ShipStationCredential'
import { WalletPreferences } from './WalletPreferences'
import { Notifications } from './Notifications'
import { Security } from './Security'
import { Languages } from './Languages'
import { Appearance } from './Appearance'
import { EmployeeTracking } from './EmployeeTracking'
import { SettingsHeaderIdentity } from './SettingsHeaderIdentity'
import { useT } from '../../i18n'
import { settingsShellI18n } from '../../i18n/settings/shell'
import { APPEARANCE_ENABLED } from '../../store/appearance'

export type SettingsSurface = 'admin' | 'seller' | 'supply'

type SectionId =
  | 'organization'
  | 'users'
  | 'employee-tracking'
  | 'plan'
  | 'shipping-tags'
  | 'shipping-printing'
  | 'store-integrations'
  | 'listing-mapping'
  | 'shipstation'
  | 'wallet-preferences'
  | 'notifications'
  | 'security'
  | 'appearance'
  | 'languages'

type SectionKey = keyof typeof settingsShellI18n.en.sections
type GroupKey = keyof typeof settingsShellI18n.en.groups

type SectionDef = {
  id: SectionId
  labelKey: SectionKey
  icon: LucideIcon
  parent?: string
  // Surfaces that may see this section. Empty = all.
  surfaces?: SettingsSurface[]
}

type SectionGroup = {
  id: string
  // Default group label key + per-surface override key.
  labelKey: GroupKey
  labelKeyBySurface?: Partial<Record<SettingsSurface, GroupKey>>
  sections: SectionDef[]
  surfaces?: SettingsSurface[]
}

const SECTION_GROUPS: SectionGroup[] = [
  {
    id: 'organization',
    labelKey: 'organization',
    sections: [
      {
        id: 'organization',
        labelKey: 'organization',
        icon: Building2,
      },
      {
        id: 'users',
        labelKey: 'users',
        icon: UsersIcon,
      },
      {
        id: 'employee-tracking',
        labelKey: 'employeeTracking',
        icon: Camera,
        surfaces: ['admin'],
      },
      {
        id: 'plan',
        labelKey: 'plan',
        icon: CreditCard,
        surfaces: ['seller'],
      },
    ],
  },
  {
    id: 'store',
    labelKey: 'store',
    labelKeyBySurface: { admin: 'platformMaster' },
    sections: [
      {
        id: 'shipping-tags',
        labelKey: 'shippingTags',
        icon: Truck,
        surfaces: ['admin', 'seller'],
      },
      {
        id: 'shipping-printing',
        labelKey: 'shippingPrinting',
        icon: Printer,
        parent: 'shipping-tags',
        surfaces: ['admin'],
      },
      {
        id: 'store-integrations',
        labelKey: 'storeIntegrations',
        icon: Plug,
        surfaces: ['seller'],
      },
      {
        id: 'listing-mapping',
        labelKey: 'listingMapping',
        icon: Link2,
        parent: 'store-integrations',
        surfaces: ['seller'],
      },
      {
        id: 'shipstation',
        labelKey: 'shipstation',
        icon: Truck,
        parent: 'store-integrations',
        surfaces: ['seller'],
      },
      {
        id: 'wallet-preferences',
        labelKey: 'walletPreferences',
        icon: Wallet,
        surfaces: ['seller'],
      },
    ],
    surfaces: ['admin', 'seller'],
  },
  {
    id: 'system',
    labelKey: 'system',
    sections: [
      {
        id: 'notifications',
        labelKey: 'notifications',
        icon: Bell,
      },
      {
        id: 'security',
        labelKey: 'security',
        icon: ShieldCheck,
      },
      {
        id: 'appearance',
        labelKey: 'appearance',
        icon: Palette,
      },
      {
        id: 'languages',
        labelKey: 'languages',
        icon: LanguagesIcon,
      },
    ],
  },
]

type ResolvedSection = {
  id: SectionId
  label: string
  icon: LucideIcon
  parent?: string
}

type VisibleGroup = {
  id: string
  label: string
  sections: ResolvedSection[]
}

// Seller Staff (BO2 admin) never sees money / plan sections (spec 2026-10-12).
const SELLER_STAFF_HIDDEN_SECTIONS: SectionId[] = ['plan', 'wallet-preferences']

function visibleSection(
  s: SectionDef,
  surface: SettingsSurface,
  isSellerStaff: boolean,
): boolean {
  if (s.id === 'appearance' && !APPEARANCE_ENABLED) return false
  if (isSellerStaff && SELLER_STAFF_HIDDEN_SECTIONS.includes(s.id)) return false
  if (!s.surfaces) return true
  return s.surfaces.includes(surface)
}

function groupsForSurface(
  surface: SettingsSurface,
  t: (typeof settingsShellI18n)['en'],
  isSellerStaff: boolean,
): VisibleGroup[] {
  return SECTION_GROUPS.filter(
    (g) => !g.surfaces || g.surfaces.includes(surface),
  )
    .map((g) => ({
      id: g.id,
      label: t.groups[g.labelKeyBySurface?.[surface] ?? g.labelKey],
      sections: g.sections
        .filter((s) => visibleSection(s, surface, isSellerStaff))
        .map<ResolvedSection>((s) => ({
          id: s.id,
          label: t.sections[s.labelKey],
          icon: s.icon,
          parent: s.parent,
        })),
    }))
    .filter((g) => g.sections.length > 0)
}

function filterGroups(groups: VisibleGroup[], query: string): VisibleGroup[] {
  const q = query.trim().toLowerCase()
  if (!q) return groups
  return groups
    .map((g) => ({
      ...g,
      sections: g.sections.filter(
        (s) =>
          s.label.toLowerCase().includes(q) ||
          g.label.toLowerCase().includes(q),
      ),
    }))
    .filter((g) => g.sections.length > 0)
}

function firstSectionId(groups: VisibleGroup[]): SectionId | null {
  return groups[0]?.sections[0]?.id ?? null
}

function findSection(
  groups: VisibleGroup[],
  id: SectionId | null,
): ResolvedSection | undefined {
  if (!id) return undefined
  for (const g of groups) {
    const s = g.sections.find((x) => x.id === id)
    if (s) return s
  }
  return undefined
}

export function Settings({ surface }: { surface: SettingsSurface }) {
  const router = useRouter()
  const t = useT(settingsShellI18n)
  const { isSellerStaff } = useMe()

  const visibleGroups = useMemo(
    () => groupsForSurface(surface, t, isSellerStaff),
    [surface, t, isSellerStaff],
  )

  const [query, setQuery] = useState('')
  const filteredGroups = useMemo(
    () => filterGroups(visibleGroups, query),
    [visibleGroups, query],
  )

  const [active, setActive] = useState<SectionId | null>(() => {
    // Deep-link support: /seller/settings?section=store-integrations lands
    // directly on that section (e.g. the "Connect a store" CTA).
    const params = new URLSearchParams(window.location.search)
    const requested = params.get('section') as SectionId | null
    if (requested && findSection(visibleGroups, requested)) return requested
    // OAuth connect-return support: the backend callbacks redirect to
    // /seller/settings?<provider>=connected|error&store=… WITHOUT a section
    // param, which used to strand the user on the first section (Organization)
    // with zero feedback. Land on Store Integrations instead so the
    // celebration modal (connected) or the error toast can actually show.
    const hasConnectResult = ['etsy', 'shopify', 'amazon', 'ebay'].some((key) =>
      params.get(key),
    )
    if (hasConnectResult && findSection(visibleGroups, 'store-integrations')) {
      return 'store-integrations'
    }
    return firstSectionId(visibleGroups)
  })

  // Keep active section pointing at something visible. If user types a query
  // that hides the current section, fall back to the first visible match
  // (adjust-during-render: the condition turns itself off after the set).
  if (filteredGroups.length > 0 && !findSection(filteredGroups, active)) {
    setActive(firstSectionId(filteredGroups))
  }

  const activeSection = findSection(visibleGroups, active)
  const appearanceChrome = APPEARANCE_ENABLED

  // Navigate to the shell root rather than history.back(): an OAuth round-trip
  // (Connect store → Etsy → backend callback → back here) pollutes the history
  // stack, so back() could land on the external Etsy/callback URL.
  const SURFACE_ROOT = { admin: '/admin', seller: '/seller', supply: '/supply' } as const
  const close = () => {
    void router.navigate({ to: SURFACE_ROOT[surface] })
  }

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div
      className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 max-sm:p-0"
      onClick={close}
    >
      <div
        className={`rounded-lg shadow-2xl w-full max-w-[1200px] h-[92vh] flex flex-col overflow-hidden max-sm:h-[100dvh] max-sm:rounded-none ${appearanceChrome ? 'bg-[var(--color-surface)] text-[var(--color-text)]' : 'bg-white text-slate-900'}`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header — surface-aware org identity + search + close */}
        <header className={`flex items-center gap-4 border-b px-6 py-4 shrink-0 max-sm:flex-wrap max-sm:gap-3 max-sm:px-4 max-sm:py-3 ${appearanceChrome ? 'border-[var(--color-border)] bg-[var(--color-surface)]' : 'border-slate-200 bg-white'}`}>
          <div className="flex items-center gap-3 min-w-0 max-sm:flex-1">
            <span className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-md ${appearanceChrome ? 'bg-[var(--color-surface-muted)] text-[var(--color-text-muted)]' : 'bg-slate-100 text-slate-600'}`}>
              <Store className="h-4 w-4" />
            </span>
            <SettingsHeaderIdentity surface={surface} themeAware={appearanceChrome} />
          </div>

          <div className="flex-1 max-w-md mx-auto max-sm:order-3 max-sm:mx-0 max-sm:w-full max-sm:max-w-none max-sm:basis-full">
            <label className="relative flex items-center">
              <Search className={`absolute left-3 h-3.5 w-3.5 ${appearanceChrome ? 'text-[var(--color-text-muted)]' : 'text-slate-400'}`} />
              <input
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t.searchPlaceholder}
                className={`w-full rounded-md border px-9 py-1.5 text-sm focus:outline-none focus:ring-2 ${appearanceChrome ? 'border-[var(--color-border)] bg-[var(--color-surface-muted)] text-[var(--color-text)] placeholder:text-[var(--color-text-muted)] focus:border-[var(--color-border-strong)] focus:ring-[var(--color-focus)]' : 'border-slate-200 bg-slate-50 placeholder:text-slate-400 focus:border-slate-300 focus:bg-white focus:ring-slate-200'}`}
              />
            </label>
          </div>

          <button
            type="button"
            onClick={close}
            className={`rounded-md p-1.5 transition-colors ${appearanceChrome ? 'text-[var(--color-text-muted)] hover:bg-[var(--color-surface-muted)] hover:text-[var(--color-text)]' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-900'}`}
            aria-label={t.closeAriaLabel}
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <div className={`border-b px-4 py-3 sm:hidden ${appearanceChrome ? 'border-[var(--color-border)] bg-[var(--color-surface-muted)]' : 'border-slate-200 bg-slate-50'}`}>
          <label htmlFor={`${surface}-settings-section`} className={`mb-1 block text-xs font-medium ${appearanceChrome ? 'text-[var(--color-text-muted)]' : 'text-slate-600'}`}>
            {t.sectionPickerLabel}
          </label>
          <select
            id={`${surface}-settings-section`}
            value={active ?? ''}
            onChange={(event) => setActive(event.target.value as SectionId)}
            className={`w-full rounded-md border px-3 py-2 text-sm ${appearanceChrome ? 'border-[var(--color-border-strong)] bg-[var(--color-surface)] text-[var(--color-text)]' : 'border-slate-300 bg-white text-slate-900'}`}
          >
            {filteredGroups.flatMap((group) => group.sections.map((section) => (
              <option key={section.id} value={section.id}>{section.label}</option>
            )))}
          </select>
        </div>

        {/* Body */}
        <div className="flex min-h-0 flex-1 overflow-hidden">
          {/* Left nav */}
          <aside className={`w-64 shrink-0 border-r overflow-y-auto py-4 max-sm:hidden ${appearanceChrome ? 'border-[var(--color-border)] bg-[var(--color-surface-muted)]' : 'border-slate-200 bg-slate-50'}`}>
            {filteredGroups.length === 0 ? (
              <div className={`px-4 py-6 text-xs ${appearanceChrome ? 'text-[var(--color-text-muted)]' : 'text-slate-500'}`}>
                {t.noMatches(query)}
              </div>
            ) : (
              filteredGroups.map((g) => (
                <div key={g.id} className="mb-5">
                  <div className="px-4 mb-1.5">
                    <span className={`text-[11px] font-semibold uppercase tracking-wider ${appearanceChrome ? 'text-[var(--color-text-muted)]' : 'text-slate-500'}`}>
                      {g.label}
                    </span>
                  </div>
                  <nav className="flex flex-col">
                    {g.sections.map((s) => {
                      const Icon = s.icon
                      const isActive = s.id === active
                      const isChild = Boolean(s.parent)
                      return (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => setActive(s.id)}
                          className={
                            'flex items-center gap-2.5 px-4 py-1.5 text-sm text-left transition-colors ' +
                            (isChild ? 'pl-9 ' : '') +
                            (isActive
                              ? (appearanceChrome ? 'bg-[var(--color-surface)] text-[var(--color-text)] border-r-2 border-[var(--color-focus)] font-medium' : 'bg-white text-slate-900 border-r-2 border-slate-900 font-medium')
                              : (appearanceChrome ? 'text-[var(--color-text-muted)] hover:bg-[var(--color-surface)]' : 'text-slate-700 hover:bg-slate-100'))
                          }
                        >
                          {isChild ? (
                            <ChevronRight className={`h-3 w-3 shrink-0 ${appearanceChrome ? 'text-[var(--color-text-muted)]' : 'text-slate-400'}`} />
                          ) : (
                            <Icon
                              className={
                                'h-4 w-4 shrink-0 ' +
                                (appearanceChrome ? 'text-[var(--color-text-muted)]' : (isActive ? 'text-slate-900' : 'text-slate-400'))
                              }
                            />
                          )}
                          <span className="flex-1 truncate">{s.label}</span>
                        </button>
                      )
                    })}
                  </nav>
                </div>
              ))
            )}
          </aside>

          {/* Right content */}
          <main className={`min-w-0 flex-1 overflow-y-auto p-6 max-sm:p-4 ${appearanceChrome ? 'bg-[var(--color-surface)] text-[var(--color-text)]' : 'bg-white'}`}>
            {!activeSection && (
              <div className="rounded-lg border border-dashed border-slate-300 bg-slate-50 p-10 text-center text-sm text-slate-500">
                {t.selectSection}
              </div>
            )}
            {activeSection?.id === 'organization' && (
              <Organization surface={surface} />
            )}
            {activeSection?.id === 'users' &&
              (surface === 'seller' ? <SellerTeam /> : <TeamManagement surface={surface} />)}
            {activeSection?.id === 'employee-tracking' && (
              <EmployeeTracking surface={surface} />
            )}
            {activeSection?.id === 'plan' && <Plan surface={surface} />}
            {activeSection?.id === 'shipping-tags' && (
              <TagManagement surface={surface} />
            )}
            {activeSection?.id === 'shipping-printing' && (
              <PrintingSetup surface={surface} />
            )}
            {activeSection?.id === 'store-integrations' && (
              <StoreIntegrations surface={surface} />
            )}
            {activeSection?.id === 'listing-mapping' && (
              <ListingMapping surface={surface} />
            )}
            {activeSection?.id === 'shipstation' && (
              <ShipStationCredential surface={surface} />
            )}
            {activeSection?.id === 'wallet-preferences' && (
              <WalletPreferences surface={surface} />
            )}
            {activeSection?.id === 'notifications' && (
              <Notifications surface={surface} />
            )}
            {activeSection?.id === 'security' && <Security surface={surface} />}
            {activeSection?.id === 'appearance' && <Appearance />}
            {activeSection?.id === 'languages' && (
              <Languages surface={surface} />
            )}
          </main>
        </div>
      </div>
    </div>
  )
}
